/**
 * 竞赛月视图日历（扩展④）：自写轻量 grid，不引第三方日历库。
 * - 里程碑按本地日期落格；逾期红、7 天内橙、其余主题色（与 CountdownBadge 同一套语义）；
 * - 月份前后留白格补满 6×7，移动端横向可滚动；
 * - 数据纯展示：日历是确定性数据的确定性展示，本模块明确不调用 LLM。
 */
import { useMemo, useState } from 'react';

import type { MilestoneDto } from '../api/types.js';
import { PHASE_LABELS } from '../lib/labels.js';

const WEEKDAYS = ['一', '二', '三', '四', '五', '六', '日'] as const;

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

/** ISO 时间 → 本地日期键 YYYY-MM-DD（日历按用户本地时区落格）。 */
function localDateKey(iso: string): string | null {
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return null;
  const d = new Date(ms);
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

interface CellDay {
  key: string;
  day: number;
  inMonth: boolean;
}

/** 生成 6 行 × 7 列（周一打头）的月格。 */
function monthGrid(year: number, monthIndex: number): CellDay[] {
  const first = new Date(year, monthIndex, 1);
  const lead = (first.getDay() + 6) % 7; // 周一=0
  const start = new Date(year, monthIndex, 1 - lead);
  const cells: CellDay[] = [];
  for (let i = 0; i < 42; i += 1) {
    const d = new Date(start.getFullYear(), start.getMonth(), start.getDate() + i);
    cells.push({
      key: `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`,
      day: d.getDate(),
      inMonth: d.getMonth() === monthIndex,
    });
  }
  return cells;
}

function milestoneTone(daysLeft: number | null): 'danger' | 'warn' | 'primary' {
  if (daysLeft !== null && daysLeft < 0) return 'danger';
  if (daysLeft !== null && daysLeft <= 7) return 'warn';
  return 'primary';
}

export function EventCalendar({ milestones }: { milestones: MilestoneDto[] }) {
  const now = new Date();
  const [cursor, setCursor] = useState({ year: now.getFullYear(), month: now.getMonth() });

  const byDay = useMemo(() => {
    const map = new Map<string, MilestoneDto[]>();
    for (const milestone of milestones) {
      const key = localDateKey(milestone.dueAt);
      if (key === null) continue;
      const list = map.get(key) ?? [];
      list.push(milestone);
      map.set(key, list);
    }
    return map;
  }, [milestones]);

  const cells = useMemo(() => monthGrid(cursor.year, cursor.month), [cursor]);
  const title = `${cursor.year} 年 ${cursor.month + 1} 月`;
  const todayKey = localDateKey(new Date().toISOString());

  const shiftMonth = (delta: number): void => {
    setCursor((prev) => {
      const next = new Date(prev.year, prev.month + delta, 1);
      return { year: next.getFullYear(), month: next.getMonth() };
    });
  };

  return (
    <div className="stack-sm">
      <div className="row-tight">
        <button type="button" className="btn btn-ghost btn-sm" aria-label="上个月" onClick={() => shiftMonth(-1)}>
          ←
        </button>
        <strong>{title}</strong>
        <button type="button" className="btn btn-ghost btn-sm" aria-label="下个月" onClick={() => shiftMonth(1)}>
          →
        </button>
        <button
          type="button"
          className="btn btn-ghost btn-sm"
          onClick={() => setCursor({ year: now.getFullYear(), month: now.getMonth() })}
        >
          回到本月
        </button>
        <span className="field-hint right">里程碑 {milestones.length} 个 · 逾期红 / 7 天内橙</span>
      </div>
      <div className="cal-scroll">
        <div className="cal-grid" role="grid" aria-label={`赛事日历 ${title}`}>
          {WEEKDAYS.map((w) => (
            <div key={w} className="cal-weekday" role="columnheader">
              {w}
            </div>
          ))}
          {cells.map((cell) => {
            const items = byDay.get(cell.key) ?? [];
            const isToday = cell.key === todayKey;
            const classNames = ['cal-cell'];
            if (!cell.inMonth) classNames.push('cal-cell-out');
            if (isToday) classNames.push('cal-cell-today');
            return (
              <div key={cell.key} className={classNames.join(' ')} role="gridcell">
                <span className="cal-day">{cell.day}</span>
                {items.map((milestone) => (
                  <span key={milestone.id} className={`cal-tag tag tag-${milestoneTone(milestone.daysLeft)}`}>
                    {milestone.label}
                  </span>
                ))}
              </div>
            );
          })}
        </div>
      </div>
      {milestones.length === 0 ? (
        <p className="muted small">本月历暂无里程碑：organizer 在「维护里程碑」里添加后，会自动落到对应日期格。</p>
      ) : null}
      <p className="field-hint">
        阶段含义：{Object.values(PHASE_LABELS).join(' / ')}（日历仅展示里程碑节点；阶段待办在队伍「项目看板」维护）。
      </p>
    </div>
  );
}
